/**
 * Centralized error handling utility
 * Provides consistent error handling with logging, user notifications, and monitoring
 */

import toast from 'react-hot-toast';

/**
 * Custom error class with application-specific context
 *
 * @example
 * throw new AppError('User not found', 'USER_NOT_FOUND', 404)
 */
export class AppError extends Error {
  /**
   * @param message - Human-readable error message
   * @param code - Machine-readable error code
   * @param statusCode - HTTP status code
   * @param originalError - Original error for wrapping
   */
  constructor(
    message: string,
    public code?: string,
    public statusCode?: number,
    public originalError?: Error
  ) {
    super(message);
    this.name = 'AppError';

    // Maintains proper stack trace (V8 engines only)
    if (typeof (Error as any).captureStackTrace === 'function') {
      (Error as any).captureStackTrace(this, AppError);
    }
  }
}

/**
 * Centralized error handler
 * Shows toast notifications, logs in development, and reports to monitoring in production
 *
 * @param error - Error to handle (Error, AppError, string, or unknown)
 * @param context - Context for debugging (e.g., 'UsersManagement.deleteUser')
 * @param showToast - Whether to show toast notification
 *
 * @example
 * try {
 *   await apiClient.deleteUser(id)
 * } catch (error) {
 *   handleError(error, 'UsersManagement.deleteUser')
 * }
 */
export function handleError(error: unknown, context?: string, showToast: boolean = true): void {
  let message = 'An unexpected error occurred';
  let shouldReport = true;

  if (error instanceof AppError) {
    message = error.message;
    // Don't report expected errors like 404s to monitoring
    shouldReport = error.statusCode !== 404;
  } else if (error instanceof Error) {
    message = error.message;
  } else if (typeof error === 'string') {
    message = error;
  }

  // Show user-friendly message via toast
  if (showToast) {
    toast.error(message);
  }

  // Log for debugging in development
  if (import.meta.env.DEV) {
    console.error(`Error${context ? ` in ${context}` : ''}:`, error);
  }

  // Report to monitoring service in production
  if (shouldReport && import.meta.env.PROD) {
    // TODO: Send to error tracking service (e.g., Sentry, Datadog, etc.)
    // Example: Sentry.captureException(error, { tags: { context } });
    console.error(`[${context || 'Unknown'}]`, error);
  }
}

/**
 * Formats API error responses into user-friendly messages
 *
 * @param errorResponse - API error response object
 * @returns Formatted error message
 *
 * @example
 * formatApiError({ error: 'Invalid credentials' }) // 'Invalid credentials'
 */
export function formatApiError(errorResponse: { error?: string; message?: string; detail?: string }): string {
  return errorResponse.error || errorResponse.message || errorResponse.detail || 'Request failed';
}

/**
 * Type guard for network errors
 *
 * @param error - Error to check
 * @returns True if network-related
 */
export function isNetworkError(error: unknown): boolean {
  if (error instanceof Error) {
    return error.message === 'Network Error' || error.message.includes('network');
  }
  return false;
}

/**
 * Type guard for timeout errors
 *
 * @param error - Error to check
 * @returns True if timeout-related
 */
export function isTimeoutError(error: unknown): boolean {
  if (error instanceof Error) {
    return error.message.includes('timeout') || error.message.includes('ECONNABORTED');
  }
  return false;
}
